import { useEffect, useMemo } from 'react';
import { useDocuments } from 'store/documents';
import { documentsToDocumentsOnTemplateView } from 'presentation/document/documentsToDocumentsOnTemplateView';
import { useInspectionStore } from '../../store';
import { prefetchDocumentThumbnails } from 'components/common/graphicUploader/documentThumbnailResolver';

interface UseInspectionTemplateInitializerOptions {
  /** Inspection initializer loads inspection + template docs in one query. */
  skipDocuments?: boolean;
}

/**
 * Binds the template of `inspectionTemplateId` to the mounted view: the store
 * reads it and follows its changes; its documents come from the documents store.
 */
export function useInspectionTemplateInitializer(
  inspectionTemplateId?: string,
  options?: UseInspectionTemplateInitializerOptions
): void {
  const skipDocuments = options?.skipDocuments ?? false;

  const openInspectionTemplate = useInspectionStore.use.openInspectionTemplate();
  const subscribeToInspectionTemplateChanges = useInspectionStore.use.subscribeToInspectionTemplateChanges();
  const setInspectionTemplateDocuments = useInspectionStore.use.setInspectionTemplateDocuments();
  const setLoadingDocuments = useInspectionStore.use.setLoadingTemplateDocuments();
  const templateId = useInspectionStore.use.templateId();

  useEffect(() => {
    if (inspectionTemplateId) {
      openInspectionTemplate(inspectionTemplateId);
    }
  }, [inspectionTemplateId, openInspectionTemplate]);

  // Documents - auto-synced via useDocuments hook
  const documentQuery = useMemo(
    () => (templateId ? { templateId } : undefined),
    [templateId]
  );
  const { documents, loading: documentsLoading } = useDocuments(
    skipDocuments ? undefined : documentQuery
  );

  useEffect(() => {
    if (skipDocuments) {
      setLoadingDocuments(false);
      return;
    }

    if (!documentQuery) {
      return;
    }

    setLoadingDocuments(documentsLoading);

    if (documentsLoading) {
      return;
    }

    const templateDocs = documentsToDocumentsOnTemplateView(documents);
    setInspectionTemplateDocuments(templateDocs);

    prefetchDocumentThumbnails([
      ...templateDocs.templateDocuments,
      ...Object.values(templateDocs.sectionDocuments).flat(),
      ...Object.values(templateDocs.checklistDocuments).flat(),
    ]);
  }, [
    documentQuery,
    documents,
    documentsLoading,
    setInspectionTemplateDocuments,
    setLoadingDocuments,
    skipDocuments,
  ]);

  useEffect(() => subscribeToInspectionTemplateChanges(), [subscribeToInspectionTemplateChanges]);
}
